import ApiClient, { ApiError } from './apiClient';

/**
 * Client für asynchrone Solver-Jobs (Milestone 6).
 *
 * Vorher hielt eine einzige POST-Anfrage bis zu 130 Sekunden offen, während der
 * Solver rechnete. Jetzt: Job einreihen -> Status pollen -> Ergebnis abholen.
 * Das Ergebnis (100-500 KB) liegt bewusst hinter einem eigenen Endpunkt — im
 * Sekundentakt gepollt wird nur der schlanke Status.
 */

/** Gepollter Job-Status (schlank; das Ergebnis liegt hinter /result). */
export interface SolverJobStatus {
  job_id?: string;
  /** Kurs des Jobs (seit Audit 2026-09-19 in 409, Status und Ergebnis enthalten) */
  class_id?: string;
  status: string;
  queue_position?: number;
  progress?: {
    solutions?: number;
    gap?: number;
    progress?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/** Ergebnis von runSolverJob — Job-Result plus jobId. */
export interface SolverJobResult {
  jobId: string;
  status: string;
  [key: string]: unknown;
}

const TERMINAL_STATUSES = new Set(['done', 'infeasible', 'failed', 'cancelled']);
const POLL_INTERVAL_MS = 1000;
// Zwei getrennte Deadlines statt einer pauschalen ab Enqueue: Die Wartezeit in
// der Queue (bis zu 5 Jobs x 120s Solver-Timeout = ~10 min sind legitim) darf
// nicht gegen das Solver-Zeitlimit zählen — sonst bricht der Client Jobs ab
// Warteposition 2 ab, bevor sie überhaupt gerechnet haben, und der
// Queue-Deckel des Backends wäre wirkungslos.
const RUNNING_DEADLINE_MS = 150000;  // ab Rechenbeginn: Solver-Timeout 120s + Puffer
const QUEUED_DEADLINE_MS = 660000;   // Sicherheitsnetz: volle Queue (600s) + Puffer
const MAX_CONSECUTIVE_ERRORS = 3;    // kurze Netzwerk-Aussetzer überbrücken

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const id = setTimeout(resolve, ms);
        if (signal) {
            signal.addEventListener('abort', () => {
                clearTimeout(id);
                reject(new DOMException('Aborted', 'AbortError'));
            }, { once: true });
        }
    });
}

export interface RunSolverJobParams {
  /** z.B. 'solver/jobs' */
  enqueueUrl: string;
  /** Request-Body (unverändert wie beim alten Endpunkt) */
  body: unknown;
  /** Status-Updates fürs Modal */
  onProgress?: (status: SolverJobStatus) => void;
  /** Abbruch (Modal geschlossen) */
  signal?: AbortSignal;
  /** Kurs, für den gerechnet wird: ein per 409 übernommener Job muss dazu passen. */
  classId?: string;
}

/** Fremder laufender Job (anderer Kurs): nicht übernehmen, sondern melden. */
export class ForeignJobError extends Error {
  jobId: string;
  classId?: string;
  constructor(jobId: string, classId?: string) {
    super('Für einen anderen Kurs läuft bereits eine Berechnung. Warte, bis sie fertig ist, oder brich sie dort ab.');
    this.name = 'ForeignJobError';
    this.jobId = jobId;
    this.classId = classId;
  }
}

/**
 * Reiht einen Solver-Job ein und pollt bis zum Ergebnis.
 *
 * @returns Ergebnis inkl. `status` ('done' | 'infeasible' | 'failed' | 'cancelled')
 */
export async function runSolverJob({ enqueueUrl, body, onProgress, signal, classId }: RunSolverJobParams): Promise<SolverJobResult> {
    let jobId: string;
    // Prüft, ob ein Job/Ergebnis zum geöffneten Kurs gehört. Ohne class_id auf einer
    // Seite (älteres Backend) wird nicht geprüft — wie vor dem Audit 2026-09-19.
    const assertSameClass = (jobClassId: unknown, id: string) => {
        if (classId && typeof jobClassId === 'string' && jobClassId && jobClassId !== classId) {
            throw new ForeignJobError(id, jobClassId);
        }
    };

    try {
        const response = await ApiClient.post(enqueueUrl, body, { signal });
        jobId = (await response.json()).job_id;
    } catch (err) {
        // 409: Es läuft bereits ein Job dieses Lehrers — daran hängen wir uns
        // (deckt auch "Seite neu geladen" ab), statt einen Fehler zu zeigen.
        // Aber nur, wenn er zum selben Kurs gehört: Sonst landete das Ergebnis
        // von Kurs A im Editor von Kurs B und ließ sich dort speichern.
        const apiErr = err as ApiError | null | undefined;
        const detail = apiErr?.originalErrorData?.detail as { job_id?: string; class_id?: string } | undefined;
        const runningJobId = detail?.job_id;
        if (apiErr?.status === 409 && runningJobId) {
            assertSameClass(detail?.class_id, runningJobId);
            jobId = runningJobId;
        } else {
            throw err;
        }
    }

    const startedAt = Date.now();
    let runningSince: number | null = null;
    let consecutiveErrors = 0;

    for (;;) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        const deadlineExceeded = runningSince !== null
            ? Date.now() - runningSince > RUNNING_DEADLINE_MS
            : Date.now() - startedAt > QUEUED_DEADLINE_MS;
        if (deadlineExceeded) {
            // Den Job serverseitig mit abräumen: Ohne das rechnete er weiter,
            // belegte den einzigen Worker-Slot und den "ein aktiver Job pro
            // Lehrer"-Lock — der Nutzer sah einen Fehler und konnte trotzdem
            // keine neue Berechnung starten, bis der Geisterjob durchgelaufen war.
            cancelSolverJob(jobId, { discard: true });

            const timeoutError = new Error('Die Berechnung hat das Zeitlimit überschritten.') as ApiError & { jobId?: string };
            timeoutError.name = 'TimeoutError';
            timeoutError.isTimeout = true;
            timeoutError.jobId = jobId;
            throw timeoutError;
        }

        let status: SolverJobStatus;
        try {
            const response = await ApiClient.get(`solver/jobs/${jobId}`, { signal });
            status = await response.json();
            consecutiveErrors = 0;
        } catch (caught) {
            const err = caught as ApiError;
            if (err.name === 'AbortError' || err.isUnauthorized) {
                throw err;
            }
            consecutiveErrors += 1;
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                throw err;
            }
            await sleep(POLL_INTERVAL_MS, signal);
            continue;
        }

        // Die Rechenzeit-Deadline startet erst, wenn der Job wirklich rechnet.
        // Zurück auf 'queued' (Requeue nach Worker-SIGTERM) setzt sie zurück.
        if (status.status === 'running') {
            if (runningSince === null) runningSince = Date.now();
        } else if (status.status === 'queued') {
            runningSince = null;
        }

        assertSameClass(status.class_id, jobId);

        if (onProgress) {
            onProgress(status);
        }

        if (TERMINAL_STATUSES.has(status.status)) {
            if (status.status === 'cancelled') {
                return { jobId, status: 'cancelled' };
            }
            const resultResponse = await ApiClient.get(`solver/jobs/${jobId}/result`, { signal });
            const result = await resultResponse.json();
            assertSameClass(result?.class_id, jobId);
            return { jobId, ...result };
        }

        await sleep(POLL_INTERVAL_MS, signal);
    }
}

/**
 * Stoppt einen laufenden Job (best effort — ein Fehler hier ist unkritisch).
 *
 * @param options.discard  true = Ergebnis verwerfen (Job endet als 'cancelled');
 *   false = besten bisher gefundenen Plan noch liefern (Job endet als 'done'
 *   mit `stopped_early: true`).
 */
export function cancelSolverJob(jobId: string | null | undefined, { discard = false }: { discard?: boolean } = {}): Promise<unknown> {
    if (!jobId) return Promise.resolve();
    return ApiClient.post(`solver/jobs/${jobId}/cancel?discard=${discard}`, {}).catch(() => {});
}

export { TERMINAL_STATUSES, POLL_INTERVAL_MS, RUNNING_DEADLINE_MS, QUEUED_DEADLINE_MS };
